import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { RatioMonitor, RatioStatus } from '../../lib/types';
import { useCursorList } from '../../lib/useCursorList';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Pagination, Spinner, StatusBadge } from '../../ui/components';

const STATUSES: RatioStatus[] = ['UNKNOWN', 'OK', 'BREACH'];

export function RatioDashboard() {
  const { persona } = useAuth();
  const navigate = useNavigate();
  const isOfficer = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_OFFICER);
  const isChief = hasAnyRole(persona?.roles, M16_ROLES.CHIEF_INSPECTOR);

  // An employer persona has no standing on the officer/chief-only dashboard
  // route (GET /ratio/dashboard) — send them straight to their own employer's
  // ratio page instead, the one place they do have standing (via mandate).
  if (!isOfficer && !isChief) {
    if (persona?.kind === 'employer' && persona.actingForBusinessId) {
      return <Navigate to={`/ratio/${persona.actingForBusinessId}`} replace />;
    }
    return <InfoNote message="Ratio monitoring requires the Labour Officer or Chief Inspector role, or an employer mandate." />;
  }

  const [status, setStatus] = useState('');
  const [jumpTo, setJumpTo] = useState('');
  const query = new URLSearchParams();
  if (status) query.set('status', status);
  const path = `/api/v1/m16/ratio/dashboard${query.toString() ? `?${query.toString()}` : ''}`;
  const listQ = useCursorList<RatioMonitor>(path);

  return (
    <div>
      <PageHeader title="Expat/Local Ratio Monitoring" subtitle="Employer headcount declarations, computed ratios and threshold-breach alerts." />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Jump to employer" className="!mb-0">
          <div className="flex gap-1.5">
            <input className="field-input" value={jumpTo} onChange={(e) => setJumpTo(e.target.value)} placeholder="Employer entity ID" />
            <button className="btn-secondary" onClick={() => jumpTo.trim() && navigate(`/ratio/${jumpTo.trim()}`)} disabled={!jumpTo.trim()}>
              Go
            </button>
          </div>
        </Field>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.items.length === 0 ? (
          <EmptyState title="No employer ratio monitors match" hint="A monitor only exists once a headcount declaration has been attested at least once." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Employer</th>
                <th className="table-head">Expat</th>
                <th className="table-head">Local</th>
                <th className="table-head">Ratio</th>
                <th className="table-head">Threshold</th>
                <th className="table-head">Source</th>
                <th className="table-head">Last evaluated</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.items.map((m) => (
                <tr key={m.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ratio/${m.employerEntityId}`)}>
                  <td className="table-cell font-mono text-xs font-semibold text-brand-600">{m.employerEntityId}</td>
                  <td className="table-cell text-xs tabular-nums">{m.expatCount}</td>
                  <td className="table-cell text-xs tabular-nums">{m.localCount}</td>
                  <td className="table-cell text-xs tabular-nums">{m.ratioPercent}%</td>
                  <td className="table-cell text-xs tabular-nums">{m.thresholdPercent}%</td>
                  <td className="table-cell text-xs">{m.source}</td>
                  <td className="table-cell text-xs">{m.lastEvaluatedAt ? formatDateTime(m.lastEvaluatedAt) : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={m.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination hasMore={listQ.hasMore} onMore={listQ.loadMore} loading={listQ.loadingMore} />
      </div>
    </div>
  );
}
